import { useState, type ChangeEvent } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Alert, Badge, Button, Card, CardBody, EmptyState, Input, LoadingState } from "@agentnative/ui";
import { apiErrorMessage } from "../lib/api/client";
import { getOrganization } from "../lib/api/org";
import { useKnownOrgsStore } from "../stores/knownOrgs";

/**
 * Staff: Organizations.
 *
 * console's OrgService has no ListOrganizations RPC, so this page cannot
 * fetch "all organizations" from the backend -- see stores/knownOrgs.ts for
 * the full explanation. Instead it offers a lookup-by-id tool (backed by
 * the real GetOrganization RPC) and remembers looked-up orgs locally as a
 * staff working set. This is a genuine gap in the current backend, not a
 * UI shortcut; it is called out here so it isn't mistaken for a finished
 * "browse all orgs" feature.
 */
export function AdminOrganizationsRoute() {
  const { ids, add, remove } = useKnownOrgsStore();
  const [lookupId, setLookupId] = useState("");
  const [activeLookup, setActiveLookup] = useState<string | null>(null);

  const lookupQuery = useQuery({
    queryKey: ["admin-org-lookup", activeLookup],
    queryFn: () => getOrganization(activeLookup!),
    enabled: !!activeLookup,
  });

  function handleLookup() {
    if (!lookupId.trim()) return;
    setActiveLookup(lookupId.trim());
  }

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold">Staff: Organizations</h1>

      <Alert variant="info" title="Look up by ID">
        This page looks organizations up by ID (GetOrganization) and remembers them in this
        browser's local working set. To search organizations by name or slug, use the picker on
        the Issue License page (staff-only ListOrganizations).
      </Alert>

      <Card padding="lg">
        <CardBody>
          <div className="flex items-end gap-3">
            <Input
              label="Organization ID"
              placeholder="org-uuid"
              value={lookupId}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setLookupId(e.target.value)}
              className="w-96"
            />
            <Button onClick={handleLookup} loading={lookupQuery.isFetching}>
              Look up
            </Button>
          </div>
          {lookupQuery.isError && (
            <div className="mt-3">
              <Alert variant="danger">{apiErrorMessage(lookupQuery.error)}</Alert>
            </div>
          )}
          {lookupQuery.data && (
            <div className="mt-4 flex items-center justify-between rounded border border-[var(--color-neutral-200)] p-3">
              <div>
                <p className="font-medium">{lookupQuery.data.organization.name}</p>
                <p className="font-mono text-xs text-[var(--color-neutral-500)]">
                  {lookupQuery.data.organization.id}
                </p>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="secondary" onClick={() => add(lookupQuery.data!.organization.id)}>
                  Save to working set
                </Button>
                <Link to="/admin/organizations/$orgId" params={{ orgId: lookupQuery.data.organization.id }}>
                  <Button size="sm">View detail</Button>
                </Link>
              </div>
            </div>
          )}
        </CardBody>
      </Card>

      <div>
        <h2 className="mb-3 text-lg font-medium">Working Set ({ids.length})</h2>
        {ids.length === 0 ? (
          <EmptyState
            title="No organizations saved yet"
            description="Use the lookup tool above to find an org and save it here."
          />
        ) : (
          <ul className="divide-y divide-[var(--color-neutral-200)] rounded border border-[var(--color-neutral-200)]">
            {ids.map((id) => (
              <KnownOrgRow key={id} orgId={id} onRemove={() => remove(id)} />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function KnownOrgRow({ orgId, onRemove }: { orgId: string; onRemove: () => void }) {
  const query = useQuery({
    queryKey: ["admin-org-lookup", orgId],
    queryFn: () => getOrganization(orgId),
  });

  return (
    <li className="flex items-center justify-between px-4 py-3">
      <div>
        {query.isLoading ? (
          <LoadingState size="sm" centered={false} />
        ) : query.isError ? (
          <span className="text-sm text-[var(--color-brand-danger)]">
            {apiErrorMessage(query.error)}
          </span>
        ) : (
          <>
            <p className="font-medium">{query.data!.organization.name}</p>
            <p className="font-mono text-xs text-[var(--color-neutral-500)]">{orgId}</p>
          </>
        )}
      </div>
      <div className="flex items-center gap-2">
        {query.data && !query.data.organization.enabled && <Badge variant="danger">Disabled</Badge>}
        <Link to="/admin/organizations/$orgId" params={{ orgId }}>
          <Button size="sm" variant="secondary">
            View
          </Button>
        </Link>
        <Button size="sm" variant="ghost" onClick={onRemove}>
          Remove
        </Button>
      </div>
    </li>
  );
}
